import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import { Star as StarIcon } from "@mui/icons-material";

import { moderateRating } from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";
import { formatDate } from "../../utils/ticketPresentation";
import { moderationErrorMessage } from "../../utils/ratingModeration";
import { tokens } from "../../utils/figmaTokens";

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

// The one door into moderateRating (spec 026 D24). Opened from the tutor drawer's
// ratings list with the row to remove; `tutor` is the drawer's selection (id, name
// and the aggregate the log records as "before"). `onRemoved(result)` hands the
// caller the server's recomputed {rating, numberOfRatings}; `onRemoved(null)`
// means the row was already gone and the list should be re-read.
const RemoveRatingDialog = ({ open, onClose, tutor, rating, actingUser, onRemoved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    if (!rating || !tutor) return;
    setSubmitting(true);
    try {
      const result = await moderateRating({ ratingId: rating.id });
      await logUserActivity({
        userId: actingUser?.uid || actingUser?.id || "unknown",
        userName: actingUser?.name || actingUser?.displayName || "Unknown User",
        userEmail: actingUser?.email || "unknown@example.com",
        // UPPER_SNAKE: the Activity Log only prettifies actions in that shape.
        action: "REMOVE_RATING",
        entityType: "tutor",
        entityId: tutor.id,
        entityName: tutor.name,
        before: {
          rating: tutor.rating ?? 0,
          numberOfRatings: tutor.numberOfRatings ?? 0,
        },
        after: {
          rating: result?.rating ?? tutor.rating ?? 0,
          numberOfRatings: result?.numberOfRatings ?? tutor.numberOfRatings ?? 0,
          removedRatingId: rating.id,
          removedStars: rating.rating,
          removedComment: rating.comment || "",
          lessonId: rating.lessonId || "",
          ratedBy: rating.ratedBy || "",
        },
        description: `${actingUser?.name || "User"} removed a ${rating.rating}-star rating from ${tutor.name}`,
      });
      notify(`Removed the rating. ${tutor.name} now averages ${result?.rating ?? "-"} over ${result?.numberOfRatings ?? "-"}.`);
      onClose();
      if (onRemoved) await onRemoved(result);
    } catch (e) {
      notify(moderationErrorMessage(e), "error");
      if (e?.code === "functions/failed-precondition") {
        onClose();
        if (onRemoved) await onRemoved(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          Remove rating{tutor?.name ? ` — ${tutor.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {rating && (
            <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <StarIcon sx={{ fontSize: 18, color: tokens.star }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {rating.rating}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {formatDate(rating.createdAt)}
                </Typography>
              </Box>
              {rating.comment ? (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {rating.comment}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No comment
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="body2">
            The rating is deleted and {tutor?.name || "the tutor"}'s average and count are
            recomputed without it, in one step. The student is not told. This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting} sx={actionButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={submitting || !rating}
            onClick={submit}
            sx={actionButtonSx}
          >
            {submitting ? "Removing..." : "Remove rating"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RemoveRatingDialog;
